(() => {
  /**
   * Get selected variant id (Horizon-safe)
   */
  function getSelectedVariantId() {
    const input =
      document.querySelector('main input[name="id"]') ||
      document.querySelector('form input[name="id"]') ||
      document.querySelector('input[name="id"]');

    if (input && input.value) return String(input.value);

    const urlVariant = new URLSearchParams(window.location.search).get("variant");
    if (urlVariant) return String(urlVariant);

    return "";
  }

  /**
   * Read Shipping Window from Liquid-generated variant map:
   * window.ofShippingWindowByVariant = { "variantId": "Ships in ...", ... }
   */
  function getShippingWindowFromVariantMapByVariantId(variantId) {
    const map = window.ofShippingWindowByVariant || {};
    const val = map[String(variantId)];
    return typeof val === "string" ? val.trim() : "";
  }

  function getShippingWindowForCurrentSelection() {
    const vid = getSelectedVariantId();
    if (!vid) return "";
    return getShippingWindowFromVariantMapByVariantId(vid);
  }

  /**
   * Merge+stamp line item properties via /cart/change.js
   * (preserves existing properties)
   */
  async function stampLineItemProperty(lineKey, shippingWindow, existingProps) {
    if (!lineKey || !shippingWindow) return;

    const merged = Object.assign({}, existingProps || {});
    merged["Shipping window"] = shippingWindow;

    await fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id: lineKey,
        properties: merged
      })
    });

    // Nudge cart UI to re-render
    document.dispatchEvent(new CustomEvent("cart:refresh"));
    document.dispatchEvent(new CustomEvent("cart:updated"));
    document.dispatchEvent(new CustomEvent("cart:change"));
  }

  /**
   * Sync ALL cart items to latest Shipping Window values
   * This fixes the "added yesterday" stale cart problem.
   */
  async function syncCartShippingWindowsToLatest() {
    // Need the variant map available (product page will have it)
    if (!window.ofShippingWindowByVariant) return;

    const cartRes = await fetch("/cart.js", { credentials: "same-origin" });
    if (!cartRes.ok) return;

    const cart = await cartRes.json();
    if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) return;

    let anyChanged = false;

    for (const item of cart.items) {
      const variantId = String(item.variant_id);
      const latest = getShippingWindowFromVariantMapByVariantId(variantId);
      if (!latest) continue;

      const current =
        item.properties && item.properties["Shipping window"]
          ? String(item.properties["Shipping window"]).trim()
          : "";

      if (current === latest) continue;

      const merged = Object.assign({}, item.properties || {});
      merged["Shipping window"] = latest;

      const resp = await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({
          id: item.key,
          properties: merged
        })
      });

      if (resp.ok) anyChanged = true;
    }

    if (anyChanged) {
      document.dispatchEvent(new CustomEvent("cart:refresh"));
      document.dispatchEvent(new CustomEvent("cart:updated"));
      document.dispatchEvent(new CustomEvent("cart:change"));
    }
  }

  /**
   * Run a sync when user clicks Checkout (drawer or cart page).
   * We do a quick preventDefault -> sync -> then navigate.
   */
  function installCheckoutClickSync() {
    document.addEventListener(
      "click",
      async (e) => {
        const a = e.target.closest('a[href^="/checkout"]');
        const btn = e.target.closest('button[name="checkout"], button[type="submit"][name="checkout"]');

        // If it's a link to /checkout OR a checkout button, intercept.
        // (Don’t intercept accelerated buttons like Shop Pay/Apple Pay; they won’t match /checkout link.)
        if (!a && !btn) return;

        // If it's the normal /checkout link, we can safely intercept
        if (a) {
          e.preventDefault();
          try {
            await syncCartShippingWindowsToLatest();
          } catch (_) {}
          window.location.href = a.href;
          return;
        }

        // If it's a checkout button inside a form, intercept and then submit
        if (btn) {
          const form = btn.closest("form");
          if (!form) return;

          e.preventDefault();
          try {
            await syncCartShippingWindowsToLatest();
          } catch (_) {}
          form.submit();
        }
      },
      true
    );
  }

  /**
   * Hook fetch() so we catch Horizon’s AJAX add-to-cart calls.
   */
  const originalFetch = window.fetch.bind(window);

  window.fetch = async function (input, init) {
    const url = typeof input === "string" ? input : (input && input.url) || "";
    const isAddToCart = /\/cart\/add(\.js)?(\?|$)/.test(url);

    // Snapshot what customer saw at click-time
    const shippingWindowAtAdd = isAddToCart ? getShippingWindowForCurrentSelection() : "";

    const res = await originalFetch(input, init);

    if (isAddToCart) {
      try {
        const data = await res.clone().json();
        const lineKey = data && data.key;
        const existingProps = (data && data.properties) ? data.properties : {};

        if (lineKey && shippingWindowAtAdd) {
          // Stamp immediately for good UX
          stampLineItemProperty(lineKey, shippingWindowAtAdd, existingProps).catch(() => {});
        }
      } catch (_) {
        // ignore
      }
    }

    return res;
  };

  /**
   * Keep cart current when customer returns (fixes stale cart issue).
   * pageshow fires on normal load AND when coming back via back/forward cache.
   */
  window.addEventListener("pageshow", () => {
    setTimeout(() => {
      syncCartShippingWindowsToLatest().catch(() => {});
    }, 300);
  });

  installCheckoutClickSync();
})();